import React, { useMemo } from 'react'

import clsx from 'clsx'
import { UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { Icon, TableCell, Tooltip, Txt } from 'src/components/atoms'
import { CompassRoleDropdown, DateTxt } from 'src/components/molecules'
import { LineLimitTxtWithTooltip } from 'src/components/molecules/texts/LineLimitTxtWithTooltip'

import { useGetExternalUsers } from 'src/hooks'
import { UpsertProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  userId: number
  compassRoles: CompassRoleEntity[]
  selectedValue: number | undefined
  isRemoveDisabled?: boolean
  errorMessage?: string
  alreadyAssignedExternalUserIds?: number[]
  assignedAt?: Date
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  trigger: UseFormTrigger<UpsertProjectFormEntity>
  className?: string
}

export function ExternalAssignUserEditTableRow({
  userId,
  compassRoles,
  selectedValue,
  isRemoveDisabled = false,
  errorMessage,
  alreadyAssignedExternalUserIds,
  assignedAt,
  watch,
  setValue,
  trigger,
  className,
}: Props) {
  const { externalUsers } = useGetExternalUsers()

  const targetUser = useMemo(
    () => externalUsers.find(u => u.id === userId),
    [externalUsers, userId]
  )

  const isShowAssignBatch = useMemo(() => {
    return !alreadyAssignedExternalUserIds?.some(id => id === userId)
  }, [alreadyAssignedExternalUserIds, userId])

  const onClickRemoveUser = async (event: React.MouseEvent) => {
    event.preventDefault()
    if (isRemoveDisabled) {
      return
    }
    const updatedUsers = watch('externalEditors').filter(user => user.id !== userId)
    setValue('externalEditors', updatedUsers)
    await trigger('externalEditors')
  }

  const onSelectCompassRole = async (compassRoleId: number | undefined) => {
    const targetCompassRole = compassRoles.find(role => role.id === compassRoleId)
    if (!targetCompassRole) {
      // あり得ないはずだが念のため
      throw new Error('compassRole not found')
    }
    const updatedUsers = watch('externalEditors').map(user => {
      if (user.id === userId) {
        return { ...user, compassRoleId, compassRolePriority: targetCompassRole.priority }
      }
      return user
    })
    setValue('externalEditors', updatedUsers)

    // 対象となるユーザーのみバリデーションの再検証を行う
    const targetIndex = watch('externalEditors').findIndex(user => user.id === userId)
    await trigger(`externalEditors.${targetIndex}`)
  }

  return (
    <div className={className}>
      <div className="tw-flex tw-gap-s0 tw-px-s0 tw-text-standardText-secondary tw-py-s-2">
        <TableCell className="tw-w-full tw-shrink tw-relative tw-h-[3.29rem]">
          <LineLimitTxtWithTooltip
            className={clsx('tw-break-all tw-text-standardText-primary')}
            text={targetUser?.name ?? '-'}
            lineLimit={2}
          />
          {isShowAssignBatch && (
            <div className="tw-absolute tw-top-s-4 -tw-left-s-3 tw-bg-errorRed-default tw-w-[10px] tw-aspect-square tw-rounded-full" />
          )}
        </TableCell>
        <TableCell width={15}>
          <LineLimitTxtWithTooltip
            className="tw-break-all"
            text={targetUser?.email ?? '-'}
            lineLimit={2}
          />
        </TableCell>
        <TableCell width={15}>
          <LineLimitTxtWithTooltip
            className="tw-break-all"
            text={
              targetUser?.keywords && targetUser.keywords.length > 0
                ? targetUser.keywords.join(',')
                : '-'
            }
            lineLimit={2}
          />
        </TableCell>
        <TableCell width={12}>
          {assignedAt ? <DateTxt date={assignedAt} /> : <Txt>-</Txt>}
        </TableCell>
        <TableCell width={28}>
          <CompassRoleDropdown
            size="m"
            compassRoles={compassRoles}
            value={selectedValue}
            selectOption={onSelectCompassRole}
            errorMessage={errorMessage}
            className="tw-mr-s-3 tw-w-full"
          />
          <button type="button" onClick={onClickRemoveUser}>
            <Tooltip
              placement="top"
              showIcon={false}
              text="プロジェクトから外す"
              disabled={isRemoveDisabled}
            >
              <Icon
                size="--r1"
                path="trash/soft/line"
                className={
                  isRemoveDisabled
                    ? 'tw-text-standardText-disable'
                    : 'tw-text-standardText-secondary hover:tw-text-standardText-primary'
                }
              />
            </Tooltip>
          </button>
        </TableCell>
      </div>
    </div>
  )
}
